/**
 * ENG-7976: map resolved integrations onto opencode `mcp` server entries.
 *
 * Follow-up to ENG-7959. The opencode adapter previously implemented no
 * `writeIntegrations`, so the manager's `/host/agent-integrations` ->
 * `frameworkAdapter.writeIntegrations` provisioning path (manager-worker.ts) was
 * silently skipped for opencode: every org/team-scoped integration and every
 * `remote_mcp` catalog integration was dropped. Managed Composio toolkits still
 * reached opencode via the SEPARATE `/host/managed-toolkits` -> `writeMcpServer`
 * path, which is why a managed toolkit worked while org/team MCPs did not.
 *
 * This module is the pure mapping the adapter's `writeIntegrations` uses. It
 * mirrors the framework-neutral, DATA-DRIVEN paths the claude-code adapter
 * renders, reusing the shared builders so the two frameworks stay in lockstep:
 *
 *   - `remoteMcp` (hosted HTTP/SSE MCP; DB catalog spec or code registry) and
 *     OAuth remote MCPs (`OAUTH_PROVIDERS[id].mcpUrl`) via `buildRemoteMcpEntry`.
 *   - data-driven native (stdio) MCPs via `buildNativeMcpEntry`.
 *
 * Deliberately OUT OF SCOPE (kept hardcoded + Claude-Code-runtime-specific in the
 * claudecode adapter, not ported here): the xero / xero-broker, cloud-broker,
 * postiz, augmented-admin, augmented-support and origami stdio entries, the xurl
 * credential store, and the `cli_tool` env_key publishing. Each needs its own
 * opencode porting decision (broker spawn env, in-process servers, CLI auth) and
 * is tracked separately. The generic credential/config env seeding below still
 * covers those integrations' `.env.integrations` vars, so an opencode agent that
 * connects one is no worse off than before this change.
 *
 * Credential handling. The generated `provision/opencode.json` MUST stay a
 * secret-free `{env:VAR}` template (it is drift-checked and its `mcp` block is
 * lifted verbatim into the serve's global config). So this maps every secret to
 * an env var seeded into `.env.integrations` and references it as `{env:VAR}` -
 * the same contract `writeChannelCredentials` already uses. The manager sources
 * `.env.integrations` into the serve env, and the runtime materializer resolves
 * the `{env:VAR}` placeholders at spawn (ENG-7956).
 */

import { buildRemoteMcpEntry } from '../../remote-mcp.js';
import { buildNativeMcpEntry } from '../../native-mcp.js';
import { INTEGRATION_REGISTRY } from '../../../integrations/registry.js';
import type { ResolvedIntegration } from '../../../types/integration.js';

export interface OpencodeIntegrationServers {
  /** MCP server entries keyed by server id, in opencode's `mcp` shape. */
  servers: Record<string, Record<string, unknown>>;
  /** Secret + config env vars to seed into `.env.integrations`. */
  envUpdates: Record<string, string>;
}

/**
 * Rewrite Claude-Code `${VAR}` spawn refs into opencode's `{env:VAR}` form.
 * Global + embedded-safe: the shared builders emit e.g. `Bearer ${TOKEN}`, and
 * opencode's materializer (materializeEnvPlaceholders) substitutes every
 * `{env:VAR}` occurrence anywhere in the string, so `Bearer {env:TOKEN}`
 * resolves correctly. A literal value with no `${...}` passes through untouched.
 */
function toOpencodeEnvRefs(value: string): string {
  return value.replace(/\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (_m, name: string) => `{env:${name}}`);
}

/** Uppercase-snake prefix for an integration's generic credential/config env vars. */
function envPrefix(definitionId: string): string {
  return definitionId.toUpperCase().replace(/[^A-Z0-9]/g, '_');
}

/**
 * Derive the env var for a structured-`auth` remote-MCP credential, matching
 * `remote-mcp.ts#credentialEnvVar` (ADR-0033 C1): `<DEFINITION_ID>_<CRED_REF>`,
 * both uppercase-snake. Replicated (not imported) because the source is a
 * module-private helper; the two must agree, so any change there mirrors here.
 */
function remoteMcpCredentialEnvVar(definitionId: string, credentialRef: string): string {
  return `${envPrefix(definitionId)}_${credentialRef.replace(/-/g, '_').toUpperCase()}`;
}

/** Convert a shared MCP entry (`${VAR}` refs) into opencode's remote-server shape. */
function toOpencodeRemoteEntry(entry: {
  url: string;
  headers?: Record<string, string>;
}): Record<string, unknown> {
  const headers = entry.headers
    ? Object.fromEntries(Object.entries(entry.headers).map(([k, v]) => [k, toOpencodeEnvRefs(v)]))
    : undefined;
  return {
    type: 'remote',
    url: entry.url,
    ...(headers && Object.keys(headers).length > 0 ? { headers } : {}),
    enabled: true,
  };
}

/** Convert a shared native (stdio) entry into opencode's local-server shape. */
function toOpencodeLocalEntry(entry: {
  command: string;
  args: string[];
  env?: Record<string, string>;
}): Record<string, unknown> {
  const environment = entry.env
    ? Object.fromEntries(Object.entries(entry.env).map(([k, v]) => [k, toOpencodeEnvRefs(v)]))
    : undefined;
  return {
    type: 'local',
    command: [entry.command, ...entry.args],
    ...(environment && Object.keys(environment).length > 0 ? { environment } : {}),
    enabled: true,
  };
}

/**
 * Pure mapping of resolved integrations to opencode MCP server entries + the
 * `.env.integrations` vars they reference. No filesystem access, so the mapping
 * is unit-testable in isolation (see opencode-integrations.test.ts).
 *
 * `ctx.agentId` threads the agent UUID into `{{agent_id}}`-templated native
 * specs; an empty string is safe for specs that don't use it (matches the
 * claude-code adapter's legacy-caller behaviour).
 */
export function buildOpencodeIntegrationServers(
  integrations: ResolvedIntegration[],
  ctx: { agentId: string; agentCodeName: string },
): OpencodeIntegrationServers {
  const servers: Record<string, Record<string, unknown>> = {};
  const envUpdates: Record<string, string> = {};

  // 1. Generic credential + config env vars, under the same <PREFIX>_ACCESS_TOKEN
  //    / <PREFIX>_API_KEY / <PREFIX>_<CONFIG_KEY> convention the claude-code
  //    adapter uses, so a shared builder's `${VAR}` header/env ref resolves.
  for (const integration of integrations) {
    const prefix = envPrefix(integration.definition_id);
    const creds = integration.credentials ?? {};

    if (integration.auth_type === 'oauth2' || integration.auth_type === 'github_app') {
      const token = creds.access_token;
      if (typeof token === 'string' && token) envUpdates[`${prefix}_ACCESS_TOKEN`] = token;
    } else if (integration.auth_type === 'api_key') {
      const token = creds.api_key;
      if (typeof token === 'string' && token) envUpdates[`${prefix}_API_KEY`] = token;
    }

    // A structured-`auth` remote MCP derives its credential var from the
    // credential FIELD name (ADR-0033), which can differ from the generic
    // convention above (e.g. a 'managed' auth_type carrying an api_key field).
    // Seed it explicitly from that field so the rendered header resolves.
    const remoteSpec =
      integration.remoteMcp ??
      INTEGRATION_REGISTRY.find((d) => d.id === integration.definition_id)?.remoteMcp;
    if (remoteSpec?.auth) {
      const ref = remoteSpec.auth.credential_ref;
      const value = creds[ref];
      if (typeof value === 'string' && value) {
        envUpdates[remoteMcpCredentialEnvVar(integration.definition_id, ref)] = value;
      }
    }

    if (integration.config) {
      for (const [key, value] of Object.entries(integration.config)) {
        if (typeof value === 'string' && value) {
          const upper = key.toUpperCase();
          const envKey = upper.startsWith(`${prefix}_`) ? upper : `${prefix}_${upper}`;
          envUpdates[envKey] = value;
        }
      }
    }
  }

  // 2. Gap-fill declared remote-MCP env defaults (a referenced-but-unset `${VAR}`
  //    must ship '' rather than a literal placeholder that corrupts the header).
  //    A real value seeded above always wins.
  for (const integration of integrations) {
    const defaults =
      integration.remoteMcp?.envDefaults ??
      INTEGRATION_REGISTRY.find((d) => d.id === integration.definition_id)?.remoteMcp?.envDefaults;
    if (!defaults) continue;
    for (const [key, value] of Object.entries(defaults)) {
      if (!(key in envUpdates)) envUpdates[key] = value;
    }
  }

  // 3. MCP server entries: a remote MCP (data-driven remoteMcp or OAuth mcpUrl)
  //    takes precedence; otherwise a data-driven native (stdio) MCP.
  for (const integration of integrations) {
    const def = INTEGRATION_REGISTRY.find((d) => d.id === integration.definition_id);

    // ENG-10614 KNOWN GAP, stated rather than silently left: this renderer has
    // NONE of the three stdio-proxy branches the claude-code adapter has - not
    // the live-header one (anchor-browser), not the OAuth one (brand-ninja,
    // granola), and not the tier-gated one added for monday. So on opencode a
    // hosted remote MCP is always a DIRECT entry and its catalogued HITL tiers
    // are NOT enforced, exactly as before that ticket.
    //
    // This is a pre-existing shortfall of the whole proxy mechanism here rather
    // than something the tier work introduced, and closing it means porting all
    // three branches plus the proxy-path plumbing, which is its own change.
    // opencode is opt-in and not the default (DEFAULT_FRAMEWORK is claude-code),
    // which bounds the exposure but does not remove it.
    const remote = buildRemoteMcpEntry(integration.definition_id, integration.remoteMcp ?? null);
    if (remote) {
      servers[integration.definition_id] = toOpencodeRemoteEntry(remote);
      continue;
    }

    if (def?.nativeMcp) {
      const key = def.nativeMcp.key ?? integration.definition_id;
      servers[key] = toOpencodeLocalEntry(
        buildNativeMcpEntry(def.nativeMcp, {
          agentId: ctx.agentId,
          agentCodeName: ctx.agentCodeName,
          integration,
        }),
      );
    }
  }

  return { servers, envUpdates };
}

// Re-export a check the manager uses to decide whether a change is structural
// (server set) vs a credential rotation. A structural change warrants a serve
// reload; a rotation should not thrash the session (the token is picked up on
// the next natural respawn, matching opencode's current behaviour).
export function opencodeIntegrationServerKeys(
  integrations: ResolvedIntegration[],
  ctx: { agentId: string; agentCodeName: string },
): string[] {
  return Object.keys(buildOpencodeIntegrationServers(integrations, ctx).servers).sort();
}
